<template>
  <b-nav-item-dropdown
    right
    no-caret
    class="header-alerts mr-2"
    menu-class="header-alerts__menu"
  >
    <template slot="button-content">
      <div class="header-alerts__trigger">
        <i class="icon-bell"></i>
        <span
          v-if="totalAlerts > 0"
          class="header-alerts__badge"
        >
          {{ totalAlerts > 9 ? '9+' : totalAlerts }}
        </span>
      </div>
    </template>

    <div class="header-alerts__header">
      <span class="header-alerts__header-title">{{ $t('alerts.title') }}</span>
      <b-badge v-if="totalAlerts > 0" variant="danger" pill class="ml-2">{{ totalAlerts }}</b-badge>
    </div>

    <div v-if="totalAlerts === 0" class="header-alerts__empty">
      <b-icon icon="bell-slash" font-scale="2" class="mb-2 text-muted"></b-icon>
      <div class="text-muted">{{ $t('alerts.noAlerts') }}</div>
    </div>

    <template v-if="unreadSupportCount > 0">
      <b-dropdown-item to="/support" class="header-alerts__item">
        <div class="header-alerts__card header-alerts__card--support">
          <div class="header-alerts__card-icon header-alerts__card-icon--support">
            <b-icon icon="life-preserver"></b-icon>
          </div>
          <div class="header-alerts__card-body">
            <div class="header-alerts__card-title">{{ $t('alerts.supportTitle') }}</div>
            <div class="header-alerts__card-desc">
              {{ $t('alerts.supportUnreadDesc', { count: unreadSupportCount }) }}
            </div>
            <div class="header-alerts__card-action">{{ $t('alerts.viewTickets') }} &rarr;</div>
          </div>
          <div class="header-alerts__card-days text-primary">
            <strong>{{ unreadSupportCount }}</strong>
            <small>{{ unreadSupportCount === 1 ? $t('alerts.ticket') : $t('alerts.tickets') }}</small>
          </div>
        </div>
      </b-dropdown-item>
    </template>

    <template v-if="$isAdmin() && pendingInvoices.length > 0">
      <b-dropdown-item to="/admin/invoices" class="header-alerts__item">
        <div class="header-alerts__card header-alerts__card--warning">
          <div class="header-alerts__card-icon header-alerts__card-icon--warning">
            <b-icon icon="exclamation-triangle-fill"></b-icon>
          </div>
          <div class="header-alerts__card-body">
            <div class="header-alerts__card-title">{{ $t('invoices.unpaidInvoices') }}</div>
            <div class="header-alerts__card-desc">
              {{ $t('alerts.unpaidInvoicesDesc', { count: pendingInvoices.length, amount: formatAmount(totalDue, currency) }) }}
            </div>
            <div class="header-alerts__card-action">{{ $t('invoices.viewInvoices') }} &rarr;</div>
          </div>
        </div>
      </b-dropdown-item>
    </template>

    <template v-if="activeTrials.length > 0">
      <div class="header-alerts__section-title">
        <b-icon icon="clock-history" class="mr-1"></b-icon>
        {{ $t('alerts.activeTrials') }}
      </div>
      <b-dropdown-item
        v-for="trial in activeTrials"
        :key="trial.uuid"
        to="/admin/projects/settings#account"
        class="header-alerts__item"
      >
        <div class="header-alerts__card header-alerts__card--info">
          <div class="header-alerts__card-icon" :class="trialIconClass(trial)">
            <b-icon icon="hourglass-split"></b-icon>
          </div>
          <div class="header-alerts__card-body">
            <div class="header-alerts__card-title">{{ getTrialProductName(trial) }}</div>
            <div class="header-alerts__card-desc" :class="trialUrgencyClass(trial)">
              {{ trialTimeRemaining(trial) }}
            </div>
          </div>
          <div class="header-alerts__card-days" :class="trialUrgencyClass(trial)">
            <template v-if="trialDaysRemaining(trial) >= 0">
              <strong>{{ trialDaysRemaining(trial) }}</strong>
              <small>{{ $t('alerts.days') }}</small>
            </template>
            <template v-else>
              <b-icon icon="x-circle-fill"></b-icon>
            </template>
          </div>
        </div>
      </b-dropdown-item>
    </template>

    <template v-if="expiringActivationCodes.length > 0">
      <div class="header-alerts__section-title">
        <b-icon icon="key" class="mr-1"></b-icon>
        {{ $t('alerts.expiringSubscriptions') }}
      </div>
      <b-dropdown-item
        v-for="group in expiringActivationCodes"
        :key="'ac-' + group.ref_no"
        :to="getActivationCodeLink(group)"
        class="header-alerts__item"
      >
        <div class="header-alerts__card header-alerts__card--warning">
          <div class="header-alerts__card-icon" :class="activationCodeIconClass(group)">
            <b-icon icon="key"></b-icon>
          </div>
          <div class="header-alerts__card-body">
            <div class="header-alerts__card-title">{{ getGroupProductName(group) }}</div>
            <div class="header-alerts__card-desc" :class="activationCodeUrgencyClass(group)">
              {{ activationCodeTimeRemaining(group) }}
            </div>
            <div class="header-alerts__card-action">{{ $t('alerts.subscribeNow') }} &rarr;</div>
          </div>
          <div class="header-alerts__card-days" :class="activationCodeUrgencyClass(group)">
            <template v-if="activationCodeDaysRemaining(group) >= 0">
              <strong>{{ activationCodeDaysRemaining(group) }}</strong>
              <small>{{ $t('alerts.days') }}</small>
            </template>
            <template v-else>
              <b-icon icon="x-circle-fill"></b-icon>
            </template>
          </div>
        </div>
      </b-dropdown-item>
    </template>
  </b-nav-item-dropdown>
</template>

<script>
export default {
  name: 'HeaderAlerts',
  data: function () {
    return {
      unreadPollTimer: null
    }
  },
  computed: {
    unreadSupportCount: function () {
      return this.$store.getters.getIntegrationBadge('protokol-support') || 0
    },
    pendingInvoices: function () {
      return this.$store.getters.getPendingInvoices || []
    },
    activeTrials: function () {
      var trials = this.$store.getters.getTrials || []
      return trials.filter(function (s) {
        return s.is_trial && s.is_active && s.expires_at
      })
    },
    expiringActivationCodes: function () {
      var subs = this.$store.getters.getSubscriptions || []
      var now = new Date()
      var horizon = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
      var expiring = subs.filter(function (s) {
        if (s.payment_provider !== 'activation_code' || !s.is_active || !s.expires_at) return false
        var expires = new Date(s.expires_at)
        return expires <= horizon
      })
      var groups = {}
      expiring.forEach(function (s) {
        var key = s.ref_no || s.uuid
        if (!groups[key]) {
          groups[key] = { ref_no: key, holder: s, expires_at: s.expires_at }
        }
        if (s.license_code) {
          groups[key].holder = s
        }
      })
      return Object.values(groups)
    },
    totalAlerts: function () {
      var count = 0
      if (this.unreadSupportCount > 0) count++
      if (this.$isAdmin() && this.pendingInvoices.length > 0) count++
      count += this.activeTrials.length
      count += this.expiringActivationCodes.length
      return count
    },
    totalDue: function () {
      return this.pendingInvoices.reduce(function (sum, inv) {
        return sum + inv.amount_due
      }, 0)
    },
    currency: function () {
      if (this.pendingInvoices.length > 0) {
        return this.pendingInvoices[0].currency
      }
      return 'USD'
    }
  },
  methods: {
    formatAmount: function (cents, currency) {
      var currencySymbols = {
        'USD': '$',
        'EUR': '€',
        'RSD': 'RSD'
      }
      var symbol = currencySymbols[currency] || currency
      var amount = (cents / 100).toFixed(2)
      if (currency === 'USD') {
        return symbol + amount
      }
      return amount + ' ' + symbol
    },
    getTrialProductName: function (trial) {
      if (trial.license_product && trial.license_product.name) {
        return trial.license_product.name
      }
      return trial.product_id
    },
    trialDaysRemaining: function (trial) {
      var now = new Date()
      var expires = new Date(trial.expires_at)
      var diff = expires.getTime() - now.getTime()
      return Math.ceil(diff / (1000 * 60 * 60 * 24))
    },
    trialTimeRemaining: function (trial) {
      var days = this.trialDaysRemaining(trial)
      if (days < 0) return this.$t('alerts.trialExpired')
      if (days === 0) return this.$t('alerts.trialExpiresToday')
      if (days === 1) return this.$t('alerts.trialExpiresIn', { days: 1 })
      return this.$t('alerts.trialExpiresIn', { days: days })
    },
    trialUrgencyClass: function (trial) {
      var days = this.trialDaysRemaining(trial)
      if (days <= 1) return 'text-danger'
      if (days <= 3) return 'text-warning'
      return 'text-muted'
    },
    trialIconClass: function (trial) {
      var days = this.trialDaysRemaining(trial)
      if (days <= 1) return 'header-alerts__card-icon--danger'
      if (days <= 3) return 'header-alerts__card-icon--warning'
      return 'header-alerts__card-icon--info'
    },
    getActivationCodeLink: function (group) {
      var s = group.holder
      var type = 'integration'
      if (s.license_product && s.license_product.group) {
        var g = s.license_product.group.toLowerCase()
        if (g === 'app' || g === 'forgeapp') type = 'app'
        else if (g === 'template') type = 'template'
      }
      return '/marketplace/install?type=' + type + '&id=' + s.product_id
    },
    getGroupProductName: function (group) {
      var s = group.holder
      if (s.license_product && s.license_product.name) return s.license_product.name
      return s.product_id
    },
    activationCodeDaysRemaining: function (group) {
      var now = new Date()
      var expires = new Date(group.expires_at)
      var diff = expires.getTime() - now.getTime()
      return Math.ceil(diff / (1000 * 60 * 60 * 24))
    },
    activationCodeTimeRemaining: function (group) {
      var days = this.activationCodeDaysRemaining(group)
      if (days < 0) return this.$t('alerts.subscriptionExpired')
      if (days === 0) return this.$t('alerts.subscriptionExpiresToday')
      return this.$t('alerts.subscriptionExpiresIn', { days: days })
    },
    activationCodeUrgencyClass: function (group) {
      var days = this.activationCodeDaysRemaining(group)
      if (days <= 7) return 'text-danger'
      return 'text-warning'
    },
    activationCodeIconClass: function (group) {
      var days = this.activationCodeDaysRemaining(group)
      if (days <= 7) return 'header-alerts__card-icon--danger'
      return 'header-alerts__card-icon--warning'
    },
    fetchUnreadCount: function () {
      var self = this
      self.$integrationApi.get('/protokol-support/support/unread-count').then(function (resp) {
        var count = resp.data && resp.data.unread_count ? resp.data.unread_count : 0
        self.$store.commit('SET_INTEGRATION_BADGE', { id: 'protokol-support', count: count })
      }).catch(function () {
        // silently ignore - user may not have support enabled
      })
    }
  },
  mounted: function () {
    this.fetchUnreadCount()
    this.unreadPollTimer = setInterval(this.fetchUnreadCount, 60000)
  },
  beforeDestroy: function () {
    if (this.unreadPollTimer) {
      clearInterval(this.unreadPollTimer)
    }
  }
}
</script>

<style scoped>
.header-alerts__trigger {
  position: relative;
  display: inline-block;
}

.header-alerts__badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background: var(--color-error-alt, #f56565);
  color: #fff;
  font-size: 0.55rem;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 10px;
  padding: 0 4px;
}

.header-alerts >>> .header-alerts__menu {
  min-width: 400px;
  max-width: 440px;
  max-height: 420px;
  overflow-y: auto;
  padding: 0;
  border: 1px solid var(--border-default, #dadce0);
  box-shadow: var(--shadow-lg);
  border-radius: var(--radius-md, 8px);
  background-color: var(--bg-card, #ffffff);
}

.header-alerts__header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-separator, #e8eaed);
  background-color: var(--bg-card, #ffffff);
}

.header-alerts__header-title {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-primary, #202124);
}

.header-alerts__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px;
  color: var(--text-muted, #80868b);
}

.header-alerts__section-title {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted, #80868b);
  padding: 10px 16px 4px;
}

.header-alerts__item {
  padding: 0;
}

.header-alerts__item >>> .dropdown-item {
  padding: 8px 12px;
  white-space: normal;
}

/* Override the global purple gradient hover for alert items */
.header-alerts__item >>> .dropdown-item:hover,
.header-alerts__item >>> .dropdown-item:focus,
.header-alerts__item >>> .dropdown-item.active {
  background-color: var(--bg-card-secondary, #f8f9fa);
  background-image: none;
  color: inherit;
}

.header-alerts__card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px;
  border-radius: var(--radius-sm, 6px);
}

.header-alerts__card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: var(--radius-sm, 6px);
  font-size: 0.85rem;
}

.header-alerts__card-icon--warning {
  background: rgba(237, 137, 54, 0.12);
  color: var(--color-warning, #ed8936);
}

.header-alerts__card-icon--info {
  background: rgba(66, 153, 225, 0.12);
  color: var(--color-info, #4299e1);
}

.header-alerts__card-icon--danger {
  background: rgba(245, 101, 101, 0.12);
  color: var(--color-error-alt, #f56565);
}

.header-alerts__card-icon--support {
  background: rgba(108, 128, 255, 0.12);
  color: var(--primary-start, #6c80ff);
}

.header-alerts__card-body {
  flex: 1;
  min-width: 0;
}

.header-alerts__card-title {
  font-weight: 600;
  font-size: 0.8rem;
  color: var(--text-primary, #202124);
  line-height: 1.3;
  margin-bottom: 1px;
}

.header-alerts__card-desc {
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--text-secondary, #5f6368);
}

.header-alerts__card-action {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--primary-start, #6c80ff);
  margin-top: 3px;
}

.header-alerts__card-days {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 36px;
  padding-top: 2px;
  line-height: 1.1;
  color: var(--text-secondary, #5f6368);
}

.header-alerts__card-days strong {
  font-size: 1.1rem;
}

.header-alerts__card-days small {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
</style>
